export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENT_BYTES = 64 * 1024;

const TEXT_EXTENSIONS = [".csv", ".tsv", ".txt", ".md", ".json"];

export const ATTACHMENT_ACCEPT = TEXT_EXTENSIONS.join(",");

export interface NexaAttachment {
  id: string;
  name: string;
  size: number;
  text: string;
}

export interface RejectedAttachment {
  name: string;
  reason: string;
}

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

const formatSize = (bytes: number) => (bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

export const describeSize = formatSize;

export const readAttachments = async (files: File[], alreadyAttached: number): Promise<{ accepted: NexaAttachment[]; rejected: RejectedAttachment[] }> => {
  const accepted: NexaAttachment[] = [];
  const rejected: RejectedAttachment[] = [];
  let slots = Math.max(0, MAX_ATTACHMENTS - alreadyAttached);

  for (const file of files) {
    if (slots === 0) {
      rejected.push({ name: file.name, reason: `Only ${MAX_ATTACHMENTS} files at a time` });
      continue;
    }
    if (!TEXT_EXTENSIONS.includes(extensionOf(file.name))) {
      rejected.push({ name: file.name, reason: "Only CSV, TSV, TXT, MD or JSON — use Bulk Upload for spreadsheets and PDFs" });
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      rejected.push({ name: file.name, reason: `Too large (${formatSize(file.size)}, max ${formatSize(MAX_ATTACHMENT_BYTES)}) — use Bulk Upload` });
      continue;
    }
    try {
      const text = await file.text();
      accepted.push({ id: `${file.name}-${file.size}-${Date.now()}-${accepted.length}`, name: file.name, size: file.size, text });
      slots -= 1;
    } catch {
      rejected.push({ name: file.name, reason: "Couldn't be read" });
    }
  }

  return { accepted, rejected };
};

export const composeMessageWithAttachments = (draft: string, attachments: NexaAttachment[]): string => {
  if (!attachments.length) return draft.trim();
  const blocks = attachments.map((a) => `--- Attached file: ${a.name} ---\n${a.text}`);
  return [draft.trim(), ...blocks].filter(Boolean).join("\n\n");
};
